<template>
  <div class="commission-record-page">
    <!-- 搜索区 -->
    <el-card shadow="never" class="search-card">
      <el-form :inline="true" :model="searchForm" class="search-form">
        <el-form-item label="用户手机号">
          <el-input
            v-model="searchForm.phone"
            placeholder="请输入手机号"
            clearable
            maxlength="11"
            style="width: 200px"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :icon="Search" @click="handleQuery">查询</el-button>
          <el-button :icon="Refresh" @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 表格：无分页，单条或空 -->
    <el-card shadow="never" class="table-card">
      <el-table
        :data="tableData"
        v-loading="loading"
        border
        style="width: 100%"
      >
        <el-table-column type="index" label="序号" width="60" align="center" :index="(i) => i + 1" />
        <el-table-column prop="userId" label="用户ID" width="100" align="center">
          <template #default="{ row }">{{ row.userId ?? '--' }}</template>
        </el-table-column>
        <el-table-column prop="nickname" label="用户昵称" min-width="100" show-overflow-tooltip>
          <template #default="{ row }">{{ row.nickname ?? '--' }}</template>
        </el-table-column>
        <el-table-column prop="userPhone" label="用户手机号" width="120" align="center">
          <template #default="{ row }">{{ row.userPhone ?? '--' }}</template>
        </el-table-column>
        <el-table-column label="直接粉丝" width="100" align="center">
          <template #default="{ row }">
            <el-link type="primary" :underline="false" class="cell-link" @click="goFans(row)">
              {{ row.firstLevelFansCount ?? '--' }}
            </el-link>
          </template>
        </el-table-column>
        <el-table-column label="间接粉丝" width="100" align="center">
          <template #default="{ row }">
            <el-link type="primary" :underline="false" class="cell-link" @click="goFans(row)">
              {{ row.secondLevelFansCount ?? '--' }}
            </el-link>
          </template>
        </el-table-column>
        <el-table-column label="有效订单" width="100" align="center">
          <template #default="{ row }">
            <el-link type="primary" :underline="false" class="cell-link" @click="goValidOrders(row)">
              {{ row.validOrderCount ?? '--' }}
            </el-link>
          </template>
        </el-table-column>
        <el-table-column label="预计佣金" width="110" align="center">
          <template #default="{ row }">¥{{ formatMoney(row.expectedCommission) }}</template>
        </el-table-column>
        <el-table-column label="已提现佣金" width="140" align="center">
          <template #default="{ row }">
            <div class="commission-cell">¥{{ formatMoney(row.withdrawnCommission) }}</div>
            <el-link type="primary" :underline="false" class="sub-link" @click="goWithdraw(row)">提现记录</el-link>
          </template>
        </el-table-column>
        <el-table-column label="冻结中佣金" width="110" align="center">
          <template #default="{ row }">¥{{ formatMoney(row.frozenCommission) }}</template>
        </el-table-column>
        <el-table-column label="余额" width="110" align="center">
          <template #default="{ row }">¥{{ formatMoney(row.balance) }}</template>
        </el-table-column>
      </el-table>
      <div v-if="!loading && !tableData.length" class="table-empty">请输入手机号并点击查询</div>
    </el-card>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue'
import { useCurrentAppInstance } from '@zto/zpage'
import { Search, Refresh } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { yuanMoney } from '@/utils/formatter'

const app = useCurrentAppInstance()

const searchForm = reactive({ phone: '' })
const loading = ref(false)
const record = ref<any>(null)

// 表格数据：有记录则为单条数组，无记录为空数组（无分页）
const tableData = ref<any[]>([])

function formatMoney (val: number | string | null | undefined): string {
  if (val == null || val === '') return '0.00'
  const n = Number(val)
  if (Number.isNaN(n)) return '0.00'
  return yuanMoney(n, { decimals: 2, thousandSep: ',', emptyText: '0.00' })
}

/** 仅在有手机号时调用接口 */
async function handleQuery () {
  const phone = (searchForm.phone || '').trim()
  if (!phone) {
    ElMessage.warning('请输入手机号')
    return
  }
  loading.value = true
  record.value = null
  tableData.value = []
  try {
    const res: any = await app.request({
      api: '/admin/dataversion/commissionRecordByPhone',
      method: 'POST',
      data: { phone }
    })
    if (res && typeof res === 'object') {
      record.value = res
      tableData.value = [res]
    } else {
      tableData.value = []
    }
  } catch (e) {
    console.error('commissionRecordByPhone error', e)
    tableData.value = []
  } finally {
    loading.value = false
  }
}

function handleReset () {
  searchForm.phone = ''
  record.value = null
  tableData.value = []
}

function rowPhone (row: any) {
  return (row?.userPhone ?? '').toString().trim()
}

/** 有效订单页，带上用户手机号 */
function goValidOrders (row: any) {
  const phone = rowPhone(row)
  if (!phone) return
  app.router.goto({
    title: '有效订单',
    path: '/commission/valid-orders',
    query: { userPhone: phone }
  })
}

/** 粉丝关系页，带上用户手机号 */
function goFans (row: any) {
  const phone = rowPhone(row)
  if (!phone) return
  app.router.goto({
    title: '粉丝关系',
    path: '/commission/fans',
    query: { userPhone: phone }
  })
}

/** 提现记录列表，带上用户手机号 */
function goWithdraw (row: any) {
  const phone = rowPhone(row)
  if (!phone) return
  app.router.goto({
    title: '提现记录',
    path: '/commission/withdraw',
    query: { userPhone: phone }
  })
}
</script>

<style lang="scss" scoped>
.commission-record-page {
  padding: 16px 24px;
  background: #f5f7fa;
  min-height: 100%;
}

.search-card {
  margin-bottom: 16px;
  border-radius: 8px;

  .search-form {
    margin-bottom: 0;

    :deep(.el-form-item) {
      margin-right: 24px;
    }
  }
}

.table-card {
  border-radius: 8px;
}

.table-empty {
  text-align: center;
  color: #909399;
  padding: 40px 0;
}

.cell-link {
  font-weight: inherit;
}

.sub-link {
  font-size: 12px;
  margin-top: 4px;
}

.commission-cell {
  text-align: center;
}
</style>
